// src/pages/support/Teams.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";

// Components
import TeamCard from "./components/TeamCard";
import Modal from "./components/Modal";
import TeamForm from "./components/TeamForm";
import ChangeCaptainForm from "./components/ChangeCaptainForm";
import SwapMembersForm from "./components/SwapMembersForm";
import DeleteTeamModal from "./components/DeleteTeamModal";
import DeactivateTeamModal from "./components/DeactivateTeamModal";
import DeactivatePlayerModal from "./components/DeactivatePlayerModal";
import RollbackTransferModal from "./components/RollbackTransferModal";
import AddInstrumentModal from "./components/AddInstrumentModal";
import AddFinalistModal from "./components/AddFinalistModal";

function Teams() {
  const [teams, setTeams] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState("1");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  // Modals state
  const [showCreate, setShowCreate] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [deleteTeamItem, setDeleteTeamItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");
  const [changeCapTeam, setChangeCapTeam] = useState(null);
  const [showSwap, setShowSwap] = useState(false);
  const [deactivateTeamItem, setDeactivateTeamItem] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateErr, setDeactivateErr] = useState("");
  const [deactivatePlayerData, setDeactivatePlayerData] = useState(null);
  const [rollbackPlayerData, setRollbackPlayerData] = useState(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [rollbackErr, setRollbackErr] = useState("");
  const [addInstrumentData, setAddInstrumentData] = useState(null);
  
  // New state for finalists
  const [finalists, setFinalists] = useState([]);
  const [showAddFinalist, setShowAddFinalist] = useState(false);

  const { apiBaseUrl } = useAuth();

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  async function fetchTeams(challengeId = "1") {
    try {
      setLoading(true);
      setErr("");
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin?challenge_id=${challengeId}`,
        { headers }
      );
      if (!res.ok) throw new Error("خطا در دریافت لیست تیم‌ها");
      const json = await res.json();
      setTeams(json?.data || []);
    } catch (e) {
      setErr(e.message || "خطای نامشخص");
    } finally {
      setLoading(false);
    }
  }

  async function fetchChallenges() {
    try {
      const res = await fetch(`${apiBaseUrl}/api/tournament/admin`, {
        headers,
      });
      if (!res.ok) throw new Error("خطا در دریافت چالش‌ها");
      const json = await res.json();
      return json?.data || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async function loadChallenges() {
    const data = await fetchChallenges();
    setChallenges(data);
  }

  async function fetchFinalists() {
    try {
      const res = await fetch(`${apiBaseUrl}/api/final/finalists`, {
        headers,
      });
      if (!res.ok) throw new Error("خطا در دریافت فینالیست‌ها");
      const json = await res.json();
      console.log("Finalists Response:", json);
      setFinalists(json?.items || []);
      return json?.items || [];
    } catch (e) {
      console.error("Error fetching finalists:", e);
      return [];
    }
  }

  async function addFinalistToTeam(teamId, userId, role) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/${teamId}/add-team-member`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            user_id: userId,
            role: role,
          }),
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "خطا در افزودن فینالیست به تیم");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function createTeam(payload) {
    const res = await fetch(`${apiBaseUrl}/api/teams/admin`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("ایجاد تیم با خطا مواجه شد");
    await fetchTeams(selectedChallengeId);
  }

  async function updateTeam(id, payload) {
    const res = await fetch(`${apiBaseUrl}/api/teams/admin/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("ویرایش تیم با خطا مواجه شد");
    await fetchTeams(selectedChallengeId);
  }

  async function changeCaptain(teamId, payload) {
    try {
      let res = await fetch(
        `${apiBaseUrl}/api/teams/admin/${teamId}/change-capitan`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const tryAlt = await fetch(
          `${apiBaseUrl}/api/teams/admin/${teamId}/change-captain`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify(payload),
          }
        );
        if (!tryAlt.ok) {
          throw new Error("تغییر کاپیتان با خطا مواجه شد");
        }
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function deleteTeam(id) {
    try {
      setIsDeleting(true);
      setDeleteErr("");
      const res = await fetch(`${apiBaseUrl}/api/teams/admin/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "حذف تیم با خطا مواجه شد");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      setDeleteErr(e.message || "خطای نامشخص");
      throw e;
    } finally {
      setIsDeleting(false);
    }
  }

  async function deactivateTeam(id) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/${id}/de-active-team`,
        {
          method: "PUT",
          headers,
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "غیرفعال کردن تیم با خطا مواجه شد");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function deactivatePlayer(userId) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/user/${userId}/de-active-player`,
        {
          method: "PUT",
          headers,
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "غیرفعال کردن بازیکن با خطا مواجه شد");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function rollbackTransfer(userId) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/user/${userId}/rollback-transfer`,
        {
          method: "PUT",
          headers,
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "بازگشت انتقال با خطا مواجه شد");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function swapMembers(payload) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/1/swap-team-member`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "تعویض اعضا با خطا مواجه شد");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  async function fetchInstruments() {
    try {
      const res = await fetch(`${apiBaseUrl}/api/teams/admin/instruments`, {
        headers,
      });
      if (!res.ok) throw new Error("خطا در دریافت ساز‌ها");
      const json = await res.json();
      return json?.data || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async function setUserInstrument(teamId, challengeId, userId, instrumentId) {
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/teams/admin/${teamId}/challenges/${challengeId}/user/${userId}/set-instrument`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ instrument_id: instrumentId }),
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "خطا در تنظیم ساز");
      }
      await fetchTeams(selectedChallengeId);
    } catch (e) {
      throw e;
    }
  }

  useEffect(() => {
    loadChallenges();
    fetchTeams(selectedChallengeId);
    fetchFinalists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // وقتی چالش تغییر کرد، تیم‌ها رو دوباره بگیر
  useEffect(() => {
    if (selectedChallengeId) {
      fetchTeams(selectedChallengeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChallengeId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return teams;
    return teams.filter((t) => (t?.name || "").toLowerCase().includes(s));
  }, [teams, q]);

  return (
    <div className="p-6" style={{ direction: "rtl" }}>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#10b981" }}
          >
            <span className="text-white text-xl">📊</span>
          </div>
          <div>
            <h2 className="text-white text-xl font-bold">مدیریت تیم‌ها</h2>
            <p className="text-gray-400 text-xs">
              خواندن، ایجاد و ویرایش تیم‌ها
            </p>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          {/* Select Challenge */}
          <select
            value={selectedChallengeId}
            onChange={(e) => setSelectedChallengeId(e.target.value)}
            className="rounded-xl bg-gray-800 border border-gray-700 text-gray-200 px-4 py-2 focus:outline-none focus:border-emerald-500"
          >
            {challenges.length === 0 ? (
              <option value="1">چالش پیش‌فرض</option>
            ) : (
              challenges.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title || c.name || `چالش ${c.id}`}
                </option>
              ))
            )}
          </select>

          <div className="relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="جستجو بر اساس نام تیم..."
              className="rounded-xl bg-gray-800 border border-gray-700 text-gray-200 px-4 py-2 w-64 focus:outline-none focus:border-emerald-500"
            />
            <span className="absolute left-3 top-2.5 text-gray-400">🔎</span>
          </div>

          <button
            onClick={() => setShowAddFinalist(true)}
            className="px-4 py-2 rounded-xl text-white font-medium"
            style={{ backgroundColor: "#8b5cf6" }}
          >
            👥 افزودن فینالیست
          </button>

          <button
            onClick={() => setShowSwap(true)}
            className="px-4 py-2 rounded-xl text-white font-medium"
            style={{ backgroundColor: "#f59e0b" }}
          >
            🔄 تعویض اعضا
          </button>

          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-xl text-white font-medium"
            style={{ backgroundColor: "#10b981" }}
          >
            + تیم جدید
          </button>
        </div>
      </div>

      {/* State */}
      {loading ? (
        <div className="py-16 text-center text-gray-300">
          در حال بارگذاری...
        </div>
      ) : err ? (
        <div className="py-6 text-center text-red-400">{err}</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-300">
          تیمی یافت نشد. از دکمه «تیم جدید» استفاده کنید.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              onEdit={() => setEditTeam(team)}
              onChangeCaptain={() => setChangeCapTeam(team)}
              onDelete={() => setDeleteTeamItem(team)}
              onDeactivate={() => setDeactivateTeamItem(team)}
              onDeactivatePlayer={(userId, username) =>
                setDeactivatePlayerData({ userId, username })
              }
              onRollbackTransfer={(userId, username) =>
                setRollbackPlayerData({ userId, username })
              }
              onAddInstrument={(teamId, userId, username) =>
                setAddInstrumentData({ teamId, userId, username })
              }
            />
          ))}
        </div>
      )}

      {/* Add Finalist Modal */}
      {showAddFinalist && (
        <Modal onClose={() => setShowAddFinalist(false)} title="افزودن فینالیست به تیم">
          <AddFinalistModal
            teams={teams}
            finalists={finalists}
            onSubmit={async (values, done, fail) => {
              try {
                await addFinalistToTeam(values.teamId, values.userId, values.role);
                done();
                setShowAddFinalist(false);
              } catch (e) {
                fail(e.message || "خطا در افزودن فینالیست");
              }
            }}
          />
        </Modal>
      )}

      {/* Swap Modal */}
      {showSwap && (
        <Modal onClose={() => setShowSwap(false)} title="تعویض اعضای دو تیم">
          <SwapMembersForm
            teams={teams}
            onSubmit={async (values, done, fail) => {
              try {
                await swapMembers(values);
                done();
                setShowSwap(false);
              } catch (e) {
                fail(e.message || "خطا در تعویض اعضا");
              }
            }}
          />
        </Modal>
      )}

      {/* Create Modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="ایجاد تیم جدید">
          <TeamForm
            onSubmit={async (values, done, fail) => {
              try {
                await createTeam(values);
                done();
                setShowCreate(false);
              } catch (e) {
                fail(e.message || "خطا در ایجاد تیم");
              }
            }}
          />
        </Modal>
      )}

      {/* Change Captain Modal */}
      {changeCapTeam && (
        <Modal
          title={`تغییر کاپیتان: ${changeCapTeam?.name || ""}`}
          onClose={() => setChangeCapTeam(null)}
        >
          <ChangeCaptainForm
            team={changeCapTeam}
            onSubmit={async (values, done, fail) => {
              try {
                await changeCaptain(changeCapTeam.id, values);
                done();
                setChangeCapTeam(null);
              } catch (e) {
                fail(e.message || "خطا در تغییر کاپیتان");
              }
            }}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {editTeam && (
        <Modal
          onClose={() => setEditTeam(null)}
          title={`ویرایش تیم: ${editTeam?.name || ""}`}
        >
          <TeamForm
            initialValues={{
              name: editTeam?.name || "",
              color: editTeam?.color || "#10b981",
              logo_path: editTeam?.logo_path || "",
              captain_user_id: editTeam?.captain_user_id || "",
              status: editTeam?.status || "active",
            }}
            onSubmit={async (values, done, fail) => {
              try {
                await updateTeam(editTeam.id, values);
                done();
                setEditTeam(null);
              } catch (e) {
                fail(e.message || "خطا در ویرایش تیم");
              }
            }}
          />
        </Modal>
      )}

      {/* Delete Team Modal */}
      <DeleteTeamModal
        team={deleteTeamItem}
        isDeleting={isDeleting}
        error={deleteErr}
        onClose={() => {
          setDeleteTeamItem(null);
          setDeleteErr("");
        }}
        onConfirm={async () => {
          try {
            await deleteTeam(deleteTeamItem.id);
            setDeleteTeamItem(null);
          } catch {}
        }}
      />

      {/* Deactivate Team Modal */}
      <DeactivateTeamModal
        team={deactivateTeamItem}
        isDeactivating={isDeactivating}
        error={deactivateErr}
        onClose={() => {
          setDeactivateTeamItem(null);
          setDeactivateErr("");
        }}
        onConfirm={async () => {
          try {
            setIsDeactivating(true);
            setDeactivateErr("");
            await deactivateTeam(deactivateTeamItem.id);
            setDeactivateTeamItem(null);
          } catch (e) {
            setDeactivateErr(e.message || "خطا در غیرفعال‌سازی");
          } finally {
            setIsDeactivating(false);
          }
        }}
      />

      {/* Deactivate Player Modal */}
      <DeactivatePlayerModal
        playerData={deactivatePlayerData}
        isDeactivating={isDeactivating}
        error={deactivateErr}
        onClose={() => {
          setDeactivatePlayerData(null);
          setDeactivateErr("");
        }}
        onConfirm={async () => {
          try {
            setIsDeactivating(true);
            setDeactivateErr("");
            await deactivatePlayer(deactivatePlayerData.userId);
            setDeactivatePlayerData(null);
          } catch (e) {
            setDeactivateErr(e.message || "خطا در غیرفعال‌سازی");
          } finally {
            setIsDeactivating(false);
          }
        }}
      />

      {/* Rollback Transfer Modal */}
      <RollbackTransferModal
        playerData={rollbackPlayerData}
        isRollingBack={isRollingBack}
        error={rollbackErr}
        onClose={() => {
          setRollbackPlayerData(null);
          setRollbackErr("");
        }}
        onConfirm={async () => {
          try {
            setIsRollingBack(true);
            setRollbackErr("");
            await rollbackTransfer(rollbackPlayerData.userId);
            setRollbackPlayerData(null);
          } catch (e) {
            setRollbackErr(e.message || "خطا در بازگشت انتقال");
          } finally {
            setIsRollingBack(false);
          }
        }}
      />

      {/* Add Instrument Modal */}
      {addInstrumentData && (
        <AddInstrumentModal
          data={addInstrumentData}
          onClose={() => setAddInstrumentData(null)}
          onSubmit={setUserInstrument}
          fetchChallenges={fetchChallenges}
          fetchInstruments={fetchInstruments}
        />
      )}
    </div>
  );
}

export default Teams;